/**
 * Profile Photo Service
 * Handles photo upload and management for user profiles
 */

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { storage, db } from '../../../services/firebase/firebase';

/**
 * Upload a profile photo
 * 
 * @param {string} userId - User ID
 * @param {File} file - Photo file to upload
 * @returns {Promise<string>} Download URL of the uploaded photo
 */
export const uploadProfilePhoto = async (userId, file) => {
  if (!userId) throw new Error('User ID is required');
  if (!file) throw new Error('File is required');
  
  try {
    // Create a unique filename
    const timestamp = Date.now();
    const filename = `${userId}_${timestamp}.${file.name.split('.').pop()}`;
    
    // Use consistent path: profile_photos (with underscore)
    const storageRef = ref(storage, `profile_photos/${userId}/${filename}`);
    
    console.log('Uploading to path:', `profile_photos/${userId}/${filename}`);
    
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    // Update the user's profile document in Firestore
    const profileRef = doc(db, 'profiles', userId);
    await updateDoc(profileRef, {
      photos: arrayUnion(downloadURL),
      lastUpdated: new Date()
    });
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading photo:', error);
    throw error;
  }
};

/**
 * Extract storage path from Firebase Storage URL
 */
const extractStoragePath = (photoURL) => {
  try {
    const url = new URL(photoURL);
    const pathPart = url.pathname.split('/o/')[1];
    if (!pathPart) {
      throw new Error('Invalid photo URL format');
    }
    return decodeURIComponent(pathPart.split('?')[0]);
  } catch (error) {
    console.error('Error extracting storage path:', error);
    throw new Error('Invalid photo URL format');
  }
};

/**
 * Delete a profile photo
 * 
 * @param {string} userId - User ID
 * @param {string} photoURL - URL of the photo to delete
 * @returns {Promise<void>} Promise that resolves when the photo is deleted
 */
export const deleteProfilePhoto = async (userId, photoURL) => {
  if (!userId) throw new Error('User ID is required');
  if (!photoURL) throw new Error('Photo URL is required');
  
  try {
    // Extract the path from the photoURL
    const path = extractStoragePath(photoURL);
    console.log('Attempting to delete path:', path);
    
    // Create a reference to the file
    const storageRef = ref(storage, path);
    
    // Delete the file from Storage
    await deleteObject(storageRef);
    
    // Update the user's profile document in Firestore
    const profileRef = doc(db, 'profiles', userId);
    await updateDoc(profileRef, {
      photos: arrayRemove(photoURL),
      lastUpdated: new Date()
    });
    
    console.log('Successfully deleted photo');
    return true;
  } catch (error) {
    console.error('Error deleting photo:', error);
    console.error('Photo URL was:', photoURL);
    throw error;
  }
};

/**
 * Set the primary profile photo (first in the array)
 * 
 * @param {string} userId - User ID
 * @param {string} photoURL - URL of the photo to set as primary
 * @param {Array} currentPhotos - Current array of photo URLs
 * @returns {Promise<Array>} Updated array of photo URLs
 */
export const setPrimaryPhoto = async (userId, photoURL, currentPhotos) => {
  if (!userId) throw new Error('User ID is required');
  if (!photoURL) throw new Error('Photo URL is required');
  if (!currentPhotos || !Array.isArray(currentPhotos)) {
    throw new Error('Current photos array is required');
  }
  
  try {
    // Remove the photo from the current position
    const filteredPhotos = currentPhotos.filter(photo => photo !== photoURL);
    
    // Add it to the beginning of the array
    const updatedPhotos = [photoURL, ...filteredPhotos];
    
    // Update the user's profile document in Firestore
    const profileRef = doc(db, 'profiles', userId);
    await updateDoc(profileRef, {
      photos: updatedPhotos,
      lastUpdated: new Date()
    });
    
    return updatedPhotos;
  } catch (error) {
    console.error('Error setting primary photo:', error);
    throw error;
  }
};

/**
 * Reorder profile photos
 * 
 * @param {string} userId - User ID
 * @param {Array} newOrder - New array of photo URLs in the desired order
 * @returns {Promise<Array>} Updated array of photo URLs
 */
export const reorderPhotos = async (userId, newOrder) => {
  if (!userId) throw new Error('User ID is required');
  if (!newOrder || !Array.isArray(newOrder)) {
    throw new Error('New photo order array is required');
  }
  
  try {
    // Update the user's profile document in Firestore
    const profileRef = doc(db, 'profiles', userId);
    await updateDoc(profileRef, {
      photos: newOrder,
      lastUpdated: new Date()
    });
    
    return newOrder;
  } catch (error) {
    console.error('Error reordering photos:', error);
    throw error;
  }
};